import React from 'react';
import useReveal from '../../hooks/useReveal';

const CookiePolicy = () => {
  document.title = 'Cookie Policy - Cloudvantage';
  const [headingRef, headingVisible] = useReveal();
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 md:px-6 text-black dark:text-white">
      <h1 ref={headingRef} className={`text-xl font-bold mb-6 ${headingVisible ? 'animate-reveal' : 'opacity-0'}`}>Cookie Policy</h1>

      <section className="mb-8">
        <p className="mb-4">
          This (“Company”) Cookie Policy (“Policy”) describes the cookies, web
          beacons, pixels, tags, scripts, tracking technologies and other
          similar technologies used by Company to collect information to enhance
          visitor experience in the course of Company’s business, including
          through Company’s websites, services, applications, tools and/or
          messaging (collectively, “Websites”). This Policy also explains a
          visitor, user or Company’s customer’s (collectively, “User”) rights to
          control a User’s access and use of the tracking technology(ies) used
          on the Websites. For additional information about Company’s privacy
          practices, please review Company’s{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>
          .
        </p>

        <p className="mb-4">
          By continuing to visit or use the Websites and any webpage(s) within
          the Websites, each User acknowledges and agrees to the use of cookies,
          tracking technologies and other similar technologies, and further
          agrees to abide by the terms of the{' '}
          <a
            href="/terms-of-use"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Universal Terms of Use
          </a>
          ,{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>
          , and/or any other terms and policies posted on the Website.
        </p>

        <p className="mb-4">
          <strong>User is in control:</strong> Other than cookies, tracking
          technologies and other similar technologies that are considered
          “essential” (see below), if a User does not wish to accept these
          technologies in connection with visiting and/or using the Websites,
          User may decline implementation of such technologies (via Company’s
          cookie pop-up on first visit to the Websites, and/or may also exercise
          any of the additional options that are described below.
        </p>

        <p className="mb-4">
          <strong>
            What are cookies, web beacons and similar technologies?
          </strong>{' '}
          These technologies are essentially small data files placed on a User’s
          computer, web-based tablet, mobile phone or other web-based electronic
          device (“collectively, a “Device”) that allows Company to record
          information when User visits or interacts with the Websites. Though
          often these technologies are generically referred to as “Cookies,”
          each functions slightly differently, and is better explained below.
        </p>

        <p className="mb-4">
          <strong>What kind of cookies are in use?</strong>
          <ul className="list-disc pl-6 mb-4">
            <li>
              “Session Cookies” are cookies that are erased when User closes
              User’s web browser. The session cookie is stored in temporary
              memory and is not retained after the browser is closed. Session
              cookies do not collect information from User’s computer.
            </li>
            <li>
              “Persistent Cookies” are stored on User’s device between
              Website(s) visits. They allow the Website(s) to remember User’s
              preferences.
            </li>
            <li>
              “First-Party Cookies” are cookies created by the Website(s) for
              use while on the Website(s).
            </li>
            <li>
              “Third-Party Cookies” are cookies created by third-parties outside
              of Company’s control and the outside of the Website(s).
            </li>
          </ul>
        </p>

        <p className="mb-4">
          <strong>Company’s Use of Cookies:</strong> Cookies, tracking
          technologies and other similar technologies are integral to the
          functionality of the Website(s) including, but not limited to, Company
          user experience, chat interface, shopping cart, and targeted
          advertising. Company also uses cookies, tracking technologies and
          other similar technologies to improve the performance of or provide
          User with extra functionality while on the Website(s). Cookies
          utilized include:
          <ul className="list-disc pl-6 mb-4">
            <li>Strictly Necessary Cookies</li>
            <li>Performance Cookies</li>
            <li>Advertising Cookies</li>
            <li>Analytical or Customization Cookies</li>
            <li>Social Media Cookies</li>
            <li>Support Technologies</li>
          </ul>
          Users may block cookies by activating certain browser settings to
          allow the refusal of the setting of all or some cookies. However,
          changing such browser settings to block all cookies (including
          essential cookies) may limit use of certain features or functions on
          the Website or access to the Cloud Service. Unless browser settings
          are adjusted to refuse cookies, the Website will reissue cookies on
          the next visit to the Websites. Please note, as further described in
          our{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>
          , Company currently does not alter our practices when Company receives
          a “Do Not Track” signal from a visitor’s browser.
        </p>

        <p className="mb-4">
          Internet browsers allow Users to change their cookie settings. These
          settings are usually found in the “options” or “preferences” menu of
          internet browsers. In order to understand these settings, the
          following links may be helpful:
          <ul className="list-disc pl-6 mb-4">
            <li>
              <a
                href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Cookie settings in Internet Explorer
              </a>
            </li>
            <li>
              <a
                href="https://support.microsoft.com/en-us/microsoft-edge/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Cookie settings for Microsoft Edge
              </a>
            </li>
            <li>
              <a
                href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Cookie settings in Firefox
              </a>
            </li>
            <li>
              <a
                href="https://support.google.com/chrome/answer/95647"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Cookie settings in Chrome
              </a>
            </li>
            <li>
              <a
                href="https://support.apple.com/en-ng/guide/safari/sfri11471/mac"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Cookie settings in Safari
              </a>
            </li>
          </ul>
          If User wishes to withdraw consent at any time, User will need to
          delete cookies using User’s internet browser settings.
        </p>

        <h5 className="text-l font-semibold mb-4">Contact Us</h5>
        <p>
          Company hopes the information in this Cookie Policy provides Users
          with clear information about the technologies used and the purposes
          for which Company uses, but if User has any additional questions, or
          requires any additional information, please review our{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>{' '}
          or contact us at privacy@cloudvantage.com.ng. By submitting a request
          User agrees to the terms of the{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>{' '}
          regarding User’s personally identifiable information.
        </p>
      </section>
    </div>
  );
};

export default CookiePolicy;
